import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
    Users, Phone, Mail, Globe, Instagram, Linkedin, Building2, MapPin, Lock, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SharedContactsPage = () => {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [requiresPassword, setRequiresPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    
    const fetchContacts = async (pwd = null) => {
        try {
            setLoading(true);
            setError(null);
            
            const url = pwd 
                ? `${API_URL}/api/public/contacts/${token}?password=${encodeURIComponent(pwd)}`
                : `${API_URL}/api/public/contacts/${token}`;
            
            const res = await axios.get(url);
            
            if (res.data.requires_password) {
                setRequiresPassword(true);
                setData({ name: res.data.name });
            } else {
                setRequiresPassword(false);
                setData(res.data);
            }
        } catch (err) {
            if (err.response?.status === 401) {
                setError('Falsches Passwort');
            } else if (err.response?.status === 404) {
                setError('Link nicht gefunden');
            } else if (err.response?.status === 410) {
                setError('Link abgelaufen');
            } else {
                setError('Fehler beim Laden');
            }
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchContacts();
    }, [token]);
    
    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        fetchContacts(password);
    };
    
    const filteredContacts = (data?.contacts || []).filter(c => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            c.name?.toLowerCase().includes(search) ||
            c.company?.toLowerCase().includes(search) ||
            c.position?.toLowerCase().includes(search)
        );
    });

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardContent className="p-8 text-center">
                        <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                        <h1 className="text-xl font-bold mb-2">{error}</h1>
                        <p className="text-muted-foreground">Dieser Link ist ungültig oder abgelaufen.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (requiresPassword) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardContent className="p-8">
                        <div className="text-center mb-6">
                            <Lock className="w-12 h-12 mx-auto text-primary mb-4" />
                            <h1 className="text-xl font-bold">{data?.name || 'Kontaktliste'}</h1>
                            <p className="text-muted-foreground mt-2">Diese Liste ist passwortgeschützt</p>
                        </div>
                        
                        <form onSubmit={handlePasswordSubmit} className="space-y-4">
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Passwort eingeben"
                                autoFocus
                            />
                            <Button type="submit" className="w-full">Öffnen</Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg mx-auto mb-4">
                        <Users className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-white">{data?.name || 'Kontakte'}</h1>
                    <p className="text-slate-400 mt-2">{data?.total} Kontakte</p>
                </div>
                
                {/* Search */}
                <div className="mb-6">
                    <div className="relative max-w-md mx-auto">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Suchen..."
                            className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                        />
                    </div>
                </div>
                
                {/* Contacts Grid */}
                <div className="grid gap-4 md:grid-cols-2">
                    {filteredContacts.map(contact => (
                        <Card key={contact.id} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-colors">
                            <CardContent className="p-4">
                                <h3 className="font-semibold text-white">{contact.name}</h3>
                                {(contact.company || contact.position) && (
                                    <p className="text-sm text-slate-400">
                                        {contact.position}{contact.position && contact.company && ' • '}{contact.company}
                                    </p>
                                )}
                                
                                <div className="mt-3 space-y-1">
                                    {contact.phones?.map((p, i) => (
                                        <a key={i} href={`tel:${p.number}`} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
                                            <Phone className="w-3 h-3" />
                                            {p.number}
                                        </a>
                                    ))}
                                    {contact.emails?.map((e, i) => (
                                        <a key={i} href={`mailto:${e.email}`} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white truncate">
                                            <Mail className="w-3 h-3 flex-shrink-0" />
                                            <span className="truncate">{e.email}</span>
                                        </a>
                                    ))}
                                    {contact.socials?.map((s, i) => (
                                        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
                                            {s.platform === 'instagram' ? <Instagram className="w-3 h-3" /> : 
                                             s.platform === 'linkedin' ? <Linkedin className="w-3 h-3" /> : 
                                             <Globe className="w-3 h-3" />}
                                            {s.platform}
                                        </a>
                                    ))}
                                </div>
                                
                                {contact.categories?.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1">
                                        {contact.categories.map(cat => (
                                            <Badge key={cat} variant="secondary" className="text-xs bg-slate-700 text-slate-300">
                                                {cat}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
                
                {filteredContacts.length === 0 && (
                    <div className="text-center py-12">
                        <Users className="w-12 h-12 mx-auto text-slate-600 mb-4" />
                        <p className="text-slate-400">Keine Kontakte gefunden</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SharedContactsPage;
